import { useEffect, useRef, useState } from 'react'
import './SearchableSelect.scss'

function SearchableSelect(props) {
  const {
    options = [],
    value = '',
    onChange,
    searchPlaceholder = 'Search',
    emptyText = 'No results found',
    className = '',
    toggleClassName = '',
    notice,
    toggleExtra,
    ariaLabel = 'Search',
  } = props

  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const rootRef = useRef(null)
  const searchRef = useRef(null)
  const selected = options.find(option => String(option.value) === String(value ?? ''))
  const query = search.trim().toLowerCase()
  const filteredOptions = options.filter(option =>
    !query || (option.label || '').toLowerCase().includes(query)
  )

  useEffect(() => {
    if (!open) return undefined

    const handleOutsideClick = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false)
        setSearch('')
      }
    }

    document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [open])

  useEffect(() => {
    setHighlightedIndex(0)
  }, [search])

  const closeMenu = () => {
    setOpen(false)
    setSearch('')
  }

  const openMenu = () => {
    setOpen(true)
    setHighlightedIndex(0)
    setTimeout(() => searchRef.current?.focus(), 0)
  }

  const selectOption = (optionValue) => {
    onChange(optionValue)
    closeMenu()
  }

  const handleSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      closeMenu()
      return
    }
    if (!filteredOptions.length) return
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setHighlightedIndex(index => Math.min(index + 1, filteredOptions.length - 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setHighlightedIndex(index => Math.max(index - 1, 0))
    } else if (event.key === 'Enter') {
      event.preventDefault()
      const option = filteredOptions[highlightedIndex]
      if (option) selectOption(option.value)
    }
  }

  return (
    <div className={`searchable-select ${className}`} ref={rootRef}>
      <button
        type="button"
        className={`searchable-select-toggle ${toggleClassName}`}
        title={selected?.label || ''}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => open ? closeMenu() : openMenu()}
      >
        <span className="searchable-select-label">{selected?.label || ''}</span>
        {toggleExtra}
      </button>
      {open &&
        <div className="searchable-select-menu" role="listbox">
          <div className="searchable-select-search">
            <i className="la la-search searchable-select-search-icon" />
            <input
              ref={searchRef}
              type="search"
              className="form-control"
              placeholder={searchPlaceholder}
              aria-label={ariaLabel}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={handleSearchKeyDown}
            />
          </div>
          {notice}
          <div className="searchable-select-options">
            {filteredOptions.map((option, index) => {
              const isSelected = String(option.value) === String(value ?? '')
              return (
                <button
                  type="button"
                  key={option.value === '' ? 'empty-option' : option.value}
                  role="option"
                  title={option.label}
                  aria-selected={isSelected}
                  className={`searchable-select-option ${index === highlightedIndex ? 'is-highlighted' : ''} ${isSelected ? 'is-selected' : ''}`}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onClick={() => selectOption(option.value)}
                >
                  <span className="searchable-select-option-label">{option.label}</span>
                  {isSelected && <i className="la la-check searchable-select-check" />}
                </button>
              )
            })}
            {!filteredOptions.length &&
              <div className="searchable-select-empty">{emptyText}</div>
            }
          </div>
        </div>
      }
    </div>
  )
}

export default SearchableSelect
